import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Lock, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : "/portal");
    } catch (err) {
      setError(err.response?.data?.detail || t("login.error"));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-none border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-4 py-16" data-testid="login-page">
      <div className="w-full max-w-md border border-slate-200 bg-white p-8">
        <div className="mb-6 flex h-12 w-12 items-center justify-center bg-blue-700 text-white"><Lock className="h-6 w-6" /></div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-950">{t("login.title")}</h1>
        <p className="mt-2 text-sm text-slate-600">{t("login.subtitle")}</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("login.email")}</label>
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("login.password")}</label>
            <div className="relative">
              <input required type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} pr-11`} data-testid="login-password" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-700" aria-label={showPassword ? "Hide password" : "Show password"} data-testid="toggle-password">
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p className="text-sm font-medium text-red-600" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} className="w-full rounded-none bg-blue-700 px-6 py-3 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-blue-800 disabled:opacity-60" data-testid="login-submit-btn">
            {loading ? "…" : t("login.submit")}
          </button>
        </form>
        <p className="mt-6 border-t border-slate-200 pt-4 text-xs text-slate-500">{t("login.hint")}</p>
      </div>
    </div>
  );
}
